import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { PALETTE as P } from "../tokens";
import { BottomNav } from "../components/BottomNav";
import { useAuth } from "../context/AuthContext";
import { rciApi } from "../api/rci";
import { protocolsApi } from "../api/protocols";
import { protocolInputsApi } from "../api/protocolInputs";
import { pickActiveProtocolId, chronologicalProtocols, protocolProgress, timeWindow, EVENING_PROTOCOL_ID } from "../utils/activeProtocol";
import { hasYesterdayCommitment, resurfacedIntention } from "../utils/screenFollowthrough";
import { Wordmark } from "../components/Wordmark";

// Lock-state rehydration is load-bearing: if the mount fetch silently
// fails, the UI shows an empty editable form to a user who's already
// submitted, inviting a second write. A 404 from getReflection is the
// normal "not yet today" case and is filtered out of Sentry; anything
// else (5xx, CORS, network, backend-not-deployed-yet, etc.) becomes a
// captured event so we see it instead of losing the lock silently.
function _reportRehydrationFailure(scope, err) {
  if (err?.status === 404) return;
  try {
    Sentry.captureException(err, {
      tags: { flow: "protocol_rehydration", scope },
      extra: { http_status: err?.status ?? "none", path: err?.path },
    });
  } catch {
    // Sentry itself misbehaving must never break the UI path.
  }
}

// ── Resume persistence ─────────────────────────────────────────────────
// Step completions are already DB-backed via /protocols/completions. We
// persist authored-step textarea drafts + the currently expanded step
// on-device so navigating away mid-protocol never drops partial text.
// Keyed by protocol id + calendar day; entries expire when the day
// rolls over. The post-protocol affect tap writes through a PATCH and
// has no draft state to persist locally.
const PROTO_STORAGE_KEY = "haiven.protocol.v1";

// User-local YYYY-MM-DD. `toISOString()` slices at UTC midnight, which
// pushed late-night completions (e.g. 11pm MT) into tomorrow's UTC day —
// the backend then scored them against a different day than the user
// experienced. Use the local calendar date instead.
function localDateKey(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
const protoTodayKey = () => localDateKey();

function loadProtoProgress(protocolId) {
  try {
    const raw = localStorage.getItem(PROTO_STORAGE_KEY);
    if (!raw) return null;
    const all = JSON.parse(raw) || {};
    const slot = all[protocolId];
    if (!slot || slot.date !== protoTodayKey()) return null;
    return slot;
  } catch { return null; }
}

function saveProtoProgress(protocolId, patch) {
  try {
    const raw = localStorage.getItem(PROTO_STORAGE_KEY);
    const all = raw ? JSON.parse(raw) : {};
    const today = protoTodayKey();
    // Drop stale day entries to keep storage small.
    Object.keys(all).forEach(k => { if (all[k]?.date !== today) delete all[k]; });
    all[protocolId] = { ...(all[protocolId] || {}), ...patch, date: today };
    localStorage.setItem(PROTO_STORAGE_KEY, JSON.stringify(all));
  } catch { /* noop */ }
}

function clearProtoProgress(protocolId) {
  try {
    const raw = localStorage.getItem(PROTO_STORAGE_KEY);
    if (!raw) return;
    const all = JSON.parse(raw) || {};
    delete all[protocolId];
    localStorage.setItem(PROTO_STORAGE_KEY, JSON.stringify(all));
  } catch { /* noop */ }
}

const DOMAIN_STYLES = {
  BIO:   { bg: "#E4DDD0", color: "#1C1814" },
  PSYCH: { bg: "#E4DDD0", color: "#1C1814" },
  BEH:   { bg: "#E4DDD0", color: "#1C1814" },
};

// Circular Progress
function CircleProgress({ pct, size = 52, stroke = 3.5 }) {
  const r = (size - stroke * 2) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - pct * circ;
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none"
        stroke={P.border} strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={r} fill="none"
        stroke={P.primary} strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: "stroke-dashoffset 0.6s cubic-bezier(0.4,0,0.2,1)" }} />
    </svg>
  );
}

// Inline progress bar for the in-progress queue / detail card state.
// Spec calls for a 4px rounded bar — muted cream track, gold fill,
// 200ms transition so a freshly-checked step animates the bar forward
// rather than jumping. ``done`` / ``total`` render as plain "X of Y"
// to the right of the bar.
function ProgressBar({ done, total }) {
  const pct = total > 0 ? (done / total) * 100 : 0;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, marginTop: 9,
    }}>
      <div style={{
        flex: 1, height: 4, borderRadius: 999,
        background: "#F2EDE3", overflow: "hidden",
      }}>
        <div style={{
          width: `${pct}%`, height: "100%",
          background: "#9E8456",
          borderRadius: 999,
          transition: "width 200ms ease",
        }} />
      </div>
      <span style={{
        fontSize: 10.5, color: P.textMute, fontWeight: 500,
        letterSpacing: "0.02em", whiteSpace: "nowrap",
        // Tabular figures so the "X of Y" label doesn't shuffle
        // horizontally as the numerator climbs.
        fontVariantNumeric: "tabular-nums",
      }}>
        {`${done} of ${total}`}
      </span>
    </div>
  );
}

// Checkbox
function Checkbox({ checked, onToggle }) {
  return (
    <button onClick={onToggle} style={{
      width: 22, height: 22, borderRadius: 6, flexShrink: 0,
      border: `1.5px solid ${checked ? P.primary : P.border}`,
      background: checked ? P.primary : "transparent",
      cursor: "pointer", transition: "all 0.2s",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {checked && (
        <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
          <path d="M2 6l3 3 5-5" stroke={P.bg} strokeWidth="1.8"
            strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
  );
}

// Styles
const STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }

  .protocol-screen {
    font-family: 'DM Sans', sans-serif;
    background: ${P.bg};
    min-height: 100vh;
    color: ${P.text};
    max-width: 420px;
    margin: 0 auto;
  }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes checkPop {
    0%   { transform: scale(1); }
    40%  { transform: scale(1.22); }
    100% { transform: scale(1); }
  }

  .fu  { animation: fadeUp 0.5s ease forwards; }
  .d1  { animation-delay: 0.05s; opacity: 0; }
  .d2  { animation-delay: 0.16s; opacity: 0; }
  .d3  { animation-delay: 0.27s; opacity: 0; }
  .d4  { animation-delay: 0.38s; opacity: 0; }
  .d5  { animation-delay: 0.50s; opacity: 0; }

  .step-row {
    display: flex; gap: 14px; align-items: flex-start;
    padding: 14px 0;
    border-bottom: 1px solid ${P.border};
    transition: opacity 0.3s;
    cursor: pointer;
  }
  .step-row:last-child { border-bottom: none; }
  .step-row.done { opacity: 0.45; }

  .protocol-card {
    background: ${P.surface}; border-radius: 20px;
    padding: 20px 20px; margin-bottom: 10px;
    box-shadow: 0 1px 14px rgba(28,24,20,0.07);
    cursor: pointer; transition: box-shadow 0.2s;
    display: flex; align-items: center;
    justify-content: space-between; gap: 14px;
  }
  .protocol-card:hover { box-shadow: 0 3px 20px rgba(28,24,20,0.11); }

  .btn-primary {
    width: 100%; padding: 17px;
    background: ${P.dark}; border: none; border-radius: 16px;
    cursor: pointer;
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 18px; font-style: italic; color: ${P.bg};
    transition: opacity 0.15s;
  }
  .btn-primary:hover { opacity: 0.85; }

  textarea {
    width: 100%; background: ${P.surface};
    border: 1.5px solid ${P.border};
    border-radius: 16px; padding: 16px 18px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13.5px; font-weight: 300; color: ${P.text};
    line-height: 1.75; resize: none; outline: none;
    transition: border-color 0.2s;
  }
  textarea:focus { border-color: ${P.primaryMid}; }
  textarea::placeholder { color: ${P.textFaint}; font-style: italic; }

  .nav-btn {
    flex: 1; background: none; border: none; cursor: pointer;
    display: flex; flex-direction: column; align-items: center; gap: 3px;
    padding: 8px 4px; transition: opacity 0.15s;
  }
  .nav-btn:hover { opacity: 0.65; }
`;

// Main Component
export default function Protocol() {
  const navigate = useNavigate();
  const { id: urlProtocolId } = useParams();
  const { user, token } = useAuth();
  const [snapshot, setSnapshot] = useState(null);
  const [protocols, setProtocols] = useState(null); // null = loading
  const [activeProtocol, setActiveProtocol] = useState(null); // null = loading
  // Time-of-day picked id, used only when the URL didn't pin one.
  const [pickedId, setPickedId] = useState(null);
  // Per-protocol completed-step count for today. Drives both the
  // active-protocol picker AND the queue ordering / "Completed today"
  // badge below — keeping it in state means the queue rerenders the
  // moment a protocol crosses its completion line.
  const [completionsByProtocol, setCompletionsByProtocol] = useState({});
  // Per-protocol *actual* rendered step count for today (from
  // /protocols/{id}/today). Static-config ``step_count`` undercounts
  // when the daily selector injects 1–2 library steps, which silently
  // flipped 4-of-5 completions to "Completed" on the queue cards. We
  // fetch the real count alongside the completions count so the queue
  // can apply strict equality (== not >=) for completion detection.
  const [stepCountByProtocol, setStepCountByProtocol] = useState({});
  // step_id → completion_id for the *active* protocol. Populated by
  // the mount fetch and by toggleStep() so the affect-tap PATCH at the
  // bottom of the screen can target a real protocol_completions row.
  const [completionIdsByStep, setCompletionIdsByStep] = useState({});
  // Mood-tap pill state for the post-protocol "How did that land?"
  // prompt. ``selectedAffect`` is the pill the user tapped (or ``null``
  // until they choose); the PATCH fires immediately and we auto-advance
  // to the submitted view 600ms later.
  const [selectedAffect, setSelectedAffect] = useState(null);

  // Pinning via URL always wins. Otherwise we resolve a time-of-day pick
  // once protocols + completions have loaded (see the effect below).
  const targetId = urlProtocolId || pickedId;

  // Evening Wind-Down is now a dedicated guided flow at /evening. The old
  // single-page checklist here is retired for it (F&F: after finishing the
  // guided ritual, the Protocol tab looped users back to a checklist of the
  // same protocol — with steps the flow never walks through, pre-checked —
  // which read as a glitch). Any route that resolves to the evening protocol
  // — the Protocol tab's evening time-of-day pick, a
  // /protocol/evening-wind-down deep link, or the Dashboard "Today's
  // protocol" card — redirects into the guided ritual. Morning/Midday
  // protocols keep using this checklist screen.
  //
  // The time-of-day pick below never lands on a COMPLETED evening (it
  // substitutes a non-evening hero so the tab stays usable as a day-review
  // surface at night), so this redirect fires in exactly two cases: an
  // explicit evening deep link, or the ritual genuinely being tonight's
  // next action.
  useEffect(() => {
    if (targetId === EVENING_PROTOCOL_ID) {
      navigate("/evening", { replace: true });
    }
  }, [targetId, navigate]);

  useEffect(() => {
    if (!user) return;
    rciApi.snapshot(user.id, token).catch(() => null).then(setSnapshot);
    protocolsApi.list(token)
      .then(data => setProtocols(data.protocols || []))
      .catch(() => setProtocols([]));
  }, [user, token]);

  // If no URL pin, fetch today's completion counts for every protocol and
  // pick the time-of-day-appropriate one that's still incomplete. Also
  // re-run when the day rolls over so the evening card shows up for
  // users who've been on the tab since morning.
  useEffect(() => {
    if (!token || !protocols || protocols.length === 0) return;
    const today = localDateKey();
    Promise.all(
      protocols.map(p =>
        Promise.all([
          protocolsApi.completions(p.id, today, token)
            .then(data => (data.completions || []).length)
            .catch(() => 0),
          // Today's *real* rendered step count, including library
          // injections. The /today endpoint persists a daily selection
          // if none exists — that's a benign side effect because the
          // selection is stable per day, so a user opening this protocol
          // later will see the same steps we just counted here.
          protocolsApi.today(p.id, today, token)
            .then(detail => (detail?.steps || []).length)
            .catch(() => p.step_count ?? 0),
        ]).then(([doneCount, totalCount]) => [p.id, doneCount, totalCount])
      )
    ).then(entries => {
      const countById = Object.fromEntries(entries.map(([id, done]) => [id, done]));
      const totalsById = Object.fromEntries(entries.map(([id, , total]) => [id, total]));
      setCompletionsByProtocol(countById);
      setStepCountByProtocol(totalsById);
      // URL pin always wins for the active card; only resolve a
      // time-of-day pick when the URL didn't supply one.
      if (!urlProtocolId) {
        let pick = pickActiveProtocolId(protocols, countById, new Date(), totalsById);
        // Evening resolves to the guided /evening flow (see the redirect
        // above). While the ritual is incomplete that redirect is the
        // right primary action — but once it's done tonight, bouncing the
        // tab to /evening would make the day's checklists unreachable all
        // evening. Substitute the latest non-evening protocol as the hero
        // so the user can still review Morning/Midday after their ritual.
        if (pick === EVENING_PROTOCOL_ID) {
          const evTotal = totalsById[EVENING_PROTOCOL_ID] ?? 0;
          const evDone = countById[EVENING_PROTOCOL_ID] ?? 0;
          if (evTotal > 0 && evDone === evTotal) {
            const fallback = chronologicalProtocols(protocols)
              .filter(p => p.id !== EVENING_PROTOCOL_ID)
              .pop();
            if (fallback) pick = fallback.id;
          }
        }
        setPickedId(pick);
      }
    });
  }, [urlProtocolId, token, protocols]);

  // Load the protocol detail merged with today's variable steps.
  // The ``/today`` endpoint returns the fixed steps from app/data
  // with 1–2 evidence-backed library entries interleaved between
  // them; each step carries a ``variable: bool`` flag the renderer
  // uses to surface a "New" pill. We pass the user's local date so
  // the server buckets against the user's calendar day, not UTC.
  useEffect(() => {
    if (!targetId || !token) return;
    protocolsApi.today(targetId, localDateKey(), token)
      .then(setActiveProtocol)
      .catch(() => setActiveProtocol(null));
  }, [targetId, token]);

  const dayCount = snapshot?.total_snapshots || '—';
  const active = activeProtocol;
  // Note: this screen no longer derives a ``checkedInToday`` predicate.
  // The daily check-in nudge that used it was removed — check-ins are
  // surfaced from Home only, and the Protocol tab houses protocols.
  // Full daily list, fixed chronological order (Morning → Midday →
  // Evening). Replaces the previous time-reactive "Up Next" queue that
  // hid missed protocols past 21:00 and pushed completed ones to the
  // bottom — F&F feedback was that the day's protocols disappearing
  // made the day's structure invisible. The badge per card (COMPLETED
  // / MISSED / UPCOMING / none-for-current) is what conveys state now.
  const queue = chronologicalProtocols(protocols || []);

  const [completed, setCompleted]   = useState({});
  const [expanded, setExpanded]     = useState(null);
  // Per-step authored text (e.g. Intention Set's textarea). Map of
  // step.id → string. Persisted to localStorage so it survives
  // navigation; saved to the backend when the user checks the step.
  const [stepInputs, setStepInputs] = useState({});
  const [submitted, setSubmitted]   = useState(false);
  const [streakData, setStreakData] = useState(null);
  // Per-step confirmation state for the inline Submit buttons on
  // authored steps. Map of step.id → boolean. Once true, the submit
  // button renders "Noted ✓" and the confirmation message appears.
  const [stepSaved, setStepSaved] = useState({});
  // Last night's evening_intention text. Pulled from /protocol-inputs/
  // recent?step_type=evening_intention and rendered above the Progress
  // Check step's textarea so the user has prompt context. Null while
  // the fetch is in flight; "" when no prior intention exists.
  const [priorEveningIntention, setPriorEveningIntention] = useState(null);

  // Did the user commit to screens-off yesterday? Gates the Morning
  // Anchor ``screen-followthrough`` step. ``null`` while the fetch is
  // in flight (default-safe — we render the step while unsure, failing
  // toward the old behaviour if the fetch never resolves), ``true`` if
  // there's a screen_commitment row dated yesterday in the user's
  // local timezone, ``false`` when there's none. Pure date-math is
  // delegated to ``hasYesterdayCommitment`` in utils/screenFollowthrough.
  const [committedYesterday, setCommittedYesterday] = useState(null);

  // Reset local state when switching protocols via URL, then rehydrate
  // any authored-step drafts + expanded step from localStorage so the
  // user returns exactly where they left off.
  useEffect(() => {
    const resume = loadProtoProgress(targetId);
    setCompleted({});
    setCompletionIdsByStep({});
    setSelectedAffect(null);
    setExpanded(resume?.expanded ?? null);
    setStepInputs(resume?.stepInputs ?? {});
    setSubmitted(false);
    setStreakData(null);
    setStepSaved({});
  }, [targetId]);

  // Fetch yesterday's evening_intention so Midday Clarity Reset's
  // Progress Check step can show "Last night you said: [X]" above
  // its textarea. 404 = user has no prior intention → render nothing.
  // Older intentions are surfaced too, relabelled by age rather than
  // dropped — see utils/screenFollowthrough.js for why the previous
  // yesterday-only gate was removed.
  useEffect(() => {
    if (!token) return;
    protocolInputsApi.recent("evening_intention", token)
      .then(row => setPriorEveningIntention(resurfacedIntention(row)))
      .catch(() => setPriorEveningIntention(null));
  }, [token]);

  // Fetch the most recent screen_commitment so Morning Anchor can
  // decide whether to show the "Screens before bed" follow-through
  // step. Delegates the date-math to ``hasYesterdayCommitment`` in
  // utils/screenFollowthrough.js where it can be unit-tested cleanly.
  // 404 → user has never committed → hide the step. Non-404 errors
  // also hide (fail-safe: better to skip than ask about a phantom
  // commitment).
  useEffect(() => {
    if (!token) return;
    protocolInputsApi.recent("screen_commitment", token)
      .then(row => setCommittedYesterday(hasYesterdayCommitment(row)))
      .catch(() => setCommittedYesterday(false));
  }, [token]);

  // Persist resumable UI state whenever it changes.
  useEffect(() => {
    if (!targetId) return;
    saveProtoProgress(targetId, { expanded, stepInputs });
  }, [targetId, expanded, stepInputs]);

  // Load today's completions + streak + authored-step inputs from
  // the server. Completion rows carry both the step_id (drives
  // checkbox state + the queue's "Completed today" pill) and a
  // completion_id (the affect-tap PATCH targets one of these), plus
  // any prior completion_affect — when present, the user already
  // tapped a pill today so the post-protocol prompt locks. Without
  // this rehydration, a page refresh after submitting would reset
  // ``submitted`` and ``stepSaved`` to empty, letting the user
  // re-submit the same intention / brain dump over the same day.
  useEffect(() => {
    if (!user || !token || !active) return;
    const today = localDateKey();

    protocolsApi.completions(active.id, today, token)
      .then(data => {
        const completedMap = {};
        const idMap = {};
        let priorAffect = null;
        (data.completions || []).forEach(c => {
          completedMap[c.step_id] = true;
          if (c.id) idMap[c.step_id] = c.id;
          // The first row we see with an affect locks the post-
          // protocol screen — once tapped, the prompt is done for the
          // day. Subsequent rows with affect (rare) just confirm.
          if (!priorAffect && c.completion_affect) {
            priorAffect = c.completion_affect;
          }
        });
        setCompleted(completedMap);
        setCompletionIdsByStep(idMap);
        if (priorAffect) {
          setSelectedAffect(priorAffect);
          setSubmitted(true);
        }
      })
      .catch(() => {});

    protocolsApi.streak(active.id, token)
      .then(setStreakData)
      .catch(() => {});

    // Authored-step inputs written today. Any row flips stepSaved +
    // pre-fills the textarea so the step renders its locked pane on
    // mount. An empty list on a day when the user DID save would
    // indicate a timezone/bucket mismatch — surface it.
    protocolInputsApi.listForDay(active.id, today, token)
      .then(rows => {
        if (!Array.isArray(rows) || rows.length === 0) return;
        const savedMap = {};
        const inputsMap = {};
        rows.forEach(r => {
          if (r?.step_id) {
            savedMap[r.step_id] = true;
            if (r.input_text) inputsMap[r.step_id] = r.input_text;
          }
        });
        if (Object.keys(savedMap).length > 0) {
          setStepSaved(prev => ({ ...prev, ...savedMap }));
        }
        if (Object.keys(inputsMap).length > 0) {
          setStepInputs(prev => ({ ...prev, ...inputsMap }));
        }
      })
      .catch(err => _reportRehydrationFailure("protocol_inputs_list", err));
  }, [user, token, active?.id]);

  const toggleStep = useCallback((stepId) => {
    if (!token || !active) return;
    const today = localDateKey();
    const wasCompleted = !!completed[stepId];

    // Optimistic update
    setCompleted(prev => ({ ...prev, [stepId]: !prev[stepId] }));

    const payload = { protocol_id: active.id, step_id: stepId, date: today };
    const request = wasCompleted
      ? protocolsApi.uncomplete(payload, token)
      : protocolsApi.complete(payload, token);

    if (wasCompleted) {
      // Drop the cached id when the user un-checks — the row is gone.
      setCompletionIdsByStep(prev => {
        const next = { ...prev };
        delete next[stepId];
        return next;
      });
    }

    request
      .then(res => {
        // Stash the freshly-created completion id so the affect-tap
        // PATCH at the bottom of the screen has a row to target.
        // ``uncomplete`` returns ``{deleted: 1}`` — no id to capture.
        if (!wasCompleted && res && typeof res.id === "string") {
          setCompletionIdsByStep(prev => ({ ...prev, [stepId]: res.id }));
        }
      })
      .catch(() => {
        // Revert on failure
        setCompleted(prev => ({ ...prev, [stepId]: wasCompleted }));
      });

    // On *check* (not uncheck), persist any authored text for this step
    // (e.g. an Intention Set entry). Best-effort: a failed reflection save
    // never undoes the step check, and an empty textarea is a no-op.
    if (!wasCompleted) {
      const text = (stepInputs[stepId] || "").trim();
      if (text) {
        protocolsApi.reflection(
          {
            protocol_id: active.id,
            step_id: stepId,
            date: today,
            reflection_text: text,
          },
          token,
        ).catch(() => {});
      }
    }
  }, [token, active, completed, stepInputs]);

  const toggleExpand = (stepId) => {
    setExpanded(prev => prev === stepId ? null : stepId);
  };

  // Inline Submit button handler for authored steps (brain_dump,
  // progress_check, morning_priority, evening_intention). Posts to the
  // generalized /protocol-inputs endpoint and flips `stepSaved[stepId]`
  // so the UI renders the confirmation. Best-effort: a failed save
  // doesn't un-flip; the user can retry on next protocol.
  const submitStepInput = useCallback(async (step, stepType) => {
    if (!active || !token) return;
    const text = (stepInputs[step.id] || "").trim();
    if (!text) return;
    try {
      await protocolInputsApi.create(
        {
          protocol_id: active.id,
          step_id: step.id,
          step_type: stepType,
          input_text: text,
          // Local calendar day so the server's idempotent-per-day
          // upsert buckets this against the user's date (not UTC).
          date: localDateKey(),
        },
        token,
      );
    } catch {
      // Silent: we keep the optimistic confirmation below. The text is
      // still in stepInputs localStorage, so the next app-open retry
      // path can resave if product decides to wire one.
    }
    setStepSaved(prev => ({ ...prev, [step.id]: true }));
  }, [token, active, stepInputs]);

  // Shared handler for non-authored interactions — the Evening
  // Wind-Down "I'm in" commitment button and the Morning Anchor
  // "Yes / Not quite" follow-through buttons both lead here. The
  // ``choice`` ("committed", "yes", "not_quite") lands in
  // protocol_inputs alongside the authored-step rows, and the step
  // itself is auto-marked complete so the user doesn't have to click
  // a separate checkbox after their answer.
  //
  // Optimistic flow: lock the UI immediately (stepSaved true, choice
  // in stepInputs for confirmation copy), fire the POST, then call
  // toggleStep if the step isn't already marked done. If the POST
  // fails silently, the next mount's listForDay rehydration reveals
  // the drift and the step un-locks for retry.
  const submitStepChoice = useCallback(async (step, stepType, choice) => {
    if (!active || !token) return;
    setStepSaved(prev => ({ ...prev, [step.id]: true }));
    setStepInputs(prev => ({ ...prev, [step.id]: choice }));
    if (!completed[step.id]) {
      toggleStep(step.id);
    }
    try {
      await protocolInputsApi.create(
        {
          protocol_id: active.id,
          step_id: step.id,
          step_type: stepType,
          input_text: choice,
          date: localDateKey(),
        },
        token,
      );
    } catch {
      // Silent — see note on submitStepInput. The step is still
      // marked done locally; next mount's rehydration resyncs.
    }
  }, [token, active, completed, toggleStep]);

  // Filter out the Morning Anchor ``screen-followthrough`` step when
  // the user has no screen_commitment row from yesterday. Asking
  // "did you follow through on last night's commitment?" makes no
  // sense when there WAS no commitment — it produces a jarring
  // false-premise prompt and a BEH signal that's actually noise.
  //
  // ``hasYesterdayCommitment === null`` (fetch in flight): leave the
  // step visible. Default-safe toward the old behaviour so a slow
  // network doesn't briefly hide and then un-hide the step.
  const rawSteps = active?.steps || [];
  const steps = rawSteps.filter(step => {
    if (step?.id !== "screen-followthrough") return true;
    return committedYesterday !== false;  // keep when true or null
  });
  const doneCount  = steps.filter(s => completed[s.id]).length;
  const totalSteps = steps.length;
  const allDone    = totalSteps > 0 && doneCount === totalSteps;
  const pct        = totalSteps > 0 ? doneCount / totalSteps : 0;

  // Mirror the active protocol's real-time progress into the
  // queue-card lookup maps. Without this, ``completionsByProtocol``
  // stays frozen at whatever the mount fetch saw — so a protocol the
  // user just finished retroactively keeps its rust "Missed" badge on
  // the card right below, because the queue's view of "done count"
  // never moved. Strict equality on the count + total maps then drives
  // the four-state badge correctly (completed / in-progress / missed /
  // not-started).
  useEffect(() => {
    if (!active?.id) return;
    setCompletionsByProtocol(prev =>
      prev[active.id] === doneCount ? prev : { ...prev, [active.id]: doneCount }
    );
    setStepCountByProtocol(prev =>
      prev[active.id] === totalSteps ? prev : { ...prev, [active.id]: totalSteps }
    );
  }, [active?.id, doneCount, totalSteps]);

  // The affect-tap PATCH attaches to a single completion row. Spec is
  // "deepest step's row first" — scan steps in reverse and take the
  // first cached id. Computed at the component scope (rather than
  // inside the affect-tap IIFE below) so the render-gate + the
  // refetch effect can both read the same value.
  const pickCompletionId = useCallback(() => {
    for (let i = steps.length - 1; i >= 0; i--) {
      const cid = completionIdsByStep[steps[i].id];
      if (cid) return cid;
    }
    return null;
  }, [steps, completionIdsByStep]);
  const affectCompletionId = pickCompletionId();

  // The affect-tap render is gated on having a completion_id (see
  // below). When ``allDone`` flips true via an optimistic checkbox
  // update, the underlying POST /protocols/complete may still be in
  // flight — the deepest step's id isn't in completionIdsByStep yet
  // and the affect tap shouldn't render. We refetch once to cover
  // that race; ``affectRefetchAttempted`` keeps us from looping when
  // the refetch genuinely finds nothing (and feeds the auto-advance
  // effect below).
  const [affectRefetchAttempted, setAffectRefetchAttempted] = useState(false);
  useEffect(() => {
    setAffectRefetchAttempted(false);
  }, [active?.id]);

  // Race-recovery refetch: allDone fires before toggleStep's POST
  // returns, so the optimistic ``completed`` flips true but the
  // matching completion_id may not have landed in our map. One
  // /completions refetch usually closes the gap.
  useEffect(() => {
    if (!allDone || !active || !token) return;
    if (affectCompletionId) return;          // already have an id
    if (affectRefetchAttempted) return;      // don't loop
    let cancelled = false;
    setAffectRefetchAttempted(true);
    protocolsApi.completions(active.id, localDateKey(), token)
      .then(data => {
        if (cancelled) return;
        const idMap = {};
        let priorAffect = null;
        (data.completions || []).forEach(c => {
          if (c.id && c.step_id) idMap[c.step_id] = c.id;
          if (!priorAffect && c.completion_affect) priorAffect = c.completion_affect;
        });
        // Merge — don't blow away an id that toggleStep's POST may
        // have written into state while the refetch was in flight.
        setCompletionIdsByStep(prev => ({ ...prev, ...idMap }));
        if (priorAffect) {
          setSelectedAffect(priorAffect);
          setSubmitted(true);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [allDone, active?.id, token, affectCompletionId, affectRefetchAttempted]);

  // Skip-and-advance: if the refetch ran and we still have no
  // completion_id, there's nothing for the PATCH to target. Don't
  // render the affect tap at all — log to Sentry with context and
  // auto-advance so the user lands on the completion state.
  useEffect(() => {
    if (!allDone || !active) return;
    if (affectCompletionId) return;
    if (!affectRefetchAttempted) return;
    if (submitted) return;
    try {
      Sentry.captureMessage("protocol_affect missing completion_id after refetch", {
        tags: { flow: "protocol_affect_patch" },
        extra: {
          protocol_id: active.id,
          steps_count: steps.length,
          completed_count: Object.values(completed).filter(Boolean).length,
        },
      });
    } catch { /* noop */ }
    setSubmitted(true);
    if (token) {
      protocolsApi.streak(active.id, token).then(setStreakData).catch(() => {});
      clearProtoProgress(active.id);
    }
  }, [allDone, active, token, affectCompletionId, affectRefetchAttempted, submitted, steps.length, completed]);

  // Rest window — between midnight and 5am local, ``pickActiveProtocolId``
  // returns ``null`` and there's no URL-pinned protocol, which means
  // the app is asleep along with the user. Render a calm "rest well"
  // card instead of "Loading protocols..." (which would never finish).
  // We require ``protocols !== null`` so the genuine loading-spinner
  // window before the protocols list arrives still works.
  const inRestWindow = (
    !urlProtocolId
    && pickedId === null
    && protocols !== null
    && timeWindow() === null
  );
  if (inRestWindow) {
    return (
      <>
        <style>{STYLES}</style>
        <div className="protocol-screen" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: 24 }}>
          <div style={{ textAlign: "center", maxWidth: 320 }}>
            <div style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 32, fontStyle: "italic", color: P.text,
              marginBottom: 16,
            }}>
              Rest well.
            </div>
            <p style={{ fontSize: 14, color: P.textMute, fontWeight: 300, lineHeight: 1.7 }}>
              Morning Anchor will be here when you wake up.
            </p>
          </div>
        </div>
      </>
    );
  }

  if (!active || protocols === null) {
    return (
      <>
        <style>{STYLES}</style>
        <div className="protocol-screen" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
          <span style={{ fontSize: 13, color: P.textMute, fontWeight: 300 }}>Loading protocols...</span>
        </div>
      </>
    );
  }

  const domainUpper = (active.domain || "").toUpperCase();
  const domainStyle = DOMAIN_STYLES[domainUpper] || DOMAIN_STYLES.BEH;

  return (
    <>
      <style>{STYLES}</style>
      <div className="protocol-screen">

        {/* Ambient wash */}
        <div style={{
          position: "fixed", top: 0, left: "50%", transform: "translateX(-50%)",
          width: 420, height: 340, pointerEvents: "none", zIndex: 0,
          background: "radial-gradient(ellipse at 40% 0%, rgba(158,132,86,0.08) 0%, transparent 65%)",
        }} />

        <div style={{ position: "relative", zIndex: 1, paddingBottom: "var(--nav-clearance)" }}>

          {/* Header */}
          <div className="fu d1" style={{
            padding: "max(54px, calc(env(safe-area-inset-top) + 12px)) 24px 0",
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <div>
              <Wordmark />
              <div style={{ fontSize: 13, color: P.textMute, fontWeight: 300 }}>
                {new Date().toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric' })}
              </div>
            </div>
            <div style={{ fontSize: 10, color: P.primary, border: `1px solid ${P.border}`,
              padding: "4px 11px", borderRadius: 99, letterSpacing: "0.07em", fontWeight: 500 }}>
              Day {dayCount}
            </div>
          </div>

          {/* Hero */}
          <div className="fu d2" style={{ padding: "28px 24px 0" }}>
            <h1 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 38, fontStyle: "italic", fontWeight: 400,
              color: P.text, lineHeight: 1.1, marginBottom: 10,
            }}>
              Active<br />
              <span style={{ color: P.primary }}>protocol.</span>
            </h1>
            <p style={{ fontSize: 13.5, color: P.textMid, fontWeight: 300,
              lineHeight: 1.7, maxWidth: 290 }}>
              One protocol at a time. Complete it, reflect on it, then move to the next.
            </p>
          </div>

          {/* Active Protocol Card */}
          <div className="fu d3" style={{ margin: "28px 24px 0" }}>
            <div style={{
              background: P.surface, borderRadius: 20,
              boxShadow: "0 1px 16px rgba(28,24,20,0.07)",
              overflow: "hidden",
            }}>
              {/* Card header */}
              <div style={{ padding: "22px 22px 18px" }}>
                <div style={{ display: "flex", justifyContent: "space-between",
                  alignItems: "flex-start", marginBottom: 16 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center",
                      gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                      <span style={{
                        fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase",
                        fontWeight: 500, padding: "3px 9px", borderRadius: 99,
                        background: domainStyle.bg,
                        color: domainStyle.color,
                      }}>
                        {domainUpper}
                      </span>
                      <span style={{ fontSize: 10, color: P.textMute }}>
                        {active.duration_minutes} min
                      </span>
                      {/* Detail-header status mirror — same gold
                          "Completed" / rust "Missed · X of Y" surfaces
                          the queue cards use. Hidden for in-progress
                          and not-started so the ring + step list carry
                          the state; the badge only fires when the user
                          needs to know they fell off or finished. */}
                      {(() => {
                        const headerProgress = protocolProgress(
                          { id: active.id, step_count: totalSteps },
                          { [active.id]: doneCount },
                          new Date(),
                          { [active.id]: totalSteps },
                        );
                        if (headerProgress.state === "completed") {
                          return (
                            <span style={{
                              fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase",
                              fontWeight: 600, padding: "3px 9px", borderRadius: 99,
                              background: "transparent",
                              color: P.primary,
                              border: `1px solid ${P.primary}`,
                            }}>
                              {"✓ Completed"}
                            </span>
                          );
                        }
                        if (headerProgress.state === "missed") {
                          return (
                            <span style={{
                              fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase",
                              fontWeight: 600, padding: "3px 9px", borderRadius: 99,
                              background: "transparent",
                              color: "#9E5A4A",
                              border: "1px solid #9E5A4A",
                            }}>
                              {headerProgress.done > 0
                                ? `Missed · ${headerProgress.done} of ${headerProgress.total}`
                                : "Missed"}
                            </span>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    <div
                      data-testid="active-protocol-hero"
                      data-protocol-id={active.id}
                      style={{
                      fontFamily: "'Playfair Display', serif",
                      fontSize: 24, fontStyle: "italic", color: P.text,
                      lineHeight: 1.15, marginBottom: 4,
                    }}>
                      {active.name}
                    </div>
                    <div style={{ fontSize: 12, color: P.textMute, fontWeight: 300 }}>
                      {active.description}
                    </div>
                  </div>

                  {/* Progress ring */}
                  <div style={{ position: "relative", flexShrink: 0, marginLeft: 14 }}>
                    <CircleProgress pct={pct} size={52} />
                    <div style={{
                      position: "absolute", inset: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <span style={{ fontFamily: "'Playfair Display', serif",
                        fontSize: 13, fontStyle: "italic", color: P.text }}>
                        {doneCount}/{totalSteps}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Streak */}
                <div style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "10px 14px", background: P.surfaceAlt,
                  borderRadius: 12,
                }}>
                  <div style={{ display: "flex", gap: 4 }}>
                    {Array.from({ length: Math.min(streakData?.current_streak ?? 0, 7) }).map((_, i) => (
                      <div key={i} style={{
                        width: 8, height: 8, borderRadius: 2,
                        background: P.primary, opacity: 0.6 + (i / 7) * 0.4,
                      }} />
                    ))}
                    {(streakData?.current_streak ?? 0) < 7 && Array.from({ length: 7 - Math.min(streakData?.current_streak ?? 0, 7) }).map((_, i) => (
                      <div key={i} style={{
                        width: 8, height: 8, borderRadius: 2,
                        background: P.border,
                      }} />
                    ))}
                  </div>
                  <span style={{ fontSize: 11, color: P.textMid, fontWeight: 300 }}>
                    {streakData?.current_streak ?? 0}-day streak &middot; best {streakData?.best_streak ?? 0}
                  </span>
                </div>
              </div>

              {/* Why divider */}
              <div style={{ borderTop: `1px solid ${P.border}`,
                padding: "16px 22px", background: "#F2EDE3" }}>
                <div style={{ fontSize: 9, color: P.primary, letterSpacing: "0.16em",
                  textTransform: "uppercase", fontWeight: 500, marginBottom: 7 }}>
                  Why this protocol
                </div>
                <p style={{ fontSize: 12.5, color: P.textMid, fontWeight: 300,
                  lineHeight: 1.65 }}>
                  {active.why}
                </p>
              </div>

              {/* Steps */}
              <div style={{ borderTop: `1px solid ${P.border}`, padding: "8px 22px 4px" }}>
                <div style={{ fontSize: 9, color: P.textMute, letterSpacing: "0.14em",
                  textTransform: "uppercase", fontWeight: 500,
                  padding: "10px 0 4px" }}>
                  Steps
                </div>
                {steps.map((step) => {
                  const done = !!completed[step.id];
                  const isOpen = expanded === step.id;
                  // Explicit interaction override — declared on the
                  // step definition in ``app/data/protocols.py``. One
                  // case today:
                  //   • followthrough (Morning Anchor "Screens before
                  //     bed") — two buttons (Yes / Not quite). Both are
                  //     acceptable answers; the honest "not quite" is
                  //     more valuable than a forced "yes".
                  // (A second shape, ``commitment``, existed for the
                  // Evening Wind-Down's Screen cutoff; the data dropped
                  // it May 19 and the evening protocol no longer renders
                  // on this screen at all — July 2026 dead-code sweep.)
                  const isFollowthrough = step.interaction === "followthrough";
                  const isCustomInteraction = isFollowthrough;
                  // Variable steps come from /protocols/{id}/today's
                  // library injection — completion-only, no input,
                  // surfaced with a "New" pill so it's clear this is
                  // today's rotating evidence-backed step (vs. the
                  // fixed protocol scaffold the user already knows).
                  const isVariable = !!step.variable;

                  // Steps that ask the user to author something themselves
                  // (rather than perform a physical action) get an inline
                  // textarea + an explicit input label so the protocol-
                  // level Reflection field at the bottom isn't mistaken
                  // for this step's response.
                  //
                  // This screen renders Morning Anchor and Midday Clarity
                  // Reset only (the evening protocol redirects to its
                  // guided /evening ritual), so the shapes here are the
                  // morning/midday ones: fixed "Priority declaration",
                  // "Brain dump", "Progress check" (title-prefix match so
                  // new authored steps only need a matching title), plus
                  // two authored variable library steps opted in by
                  // step.id — worry-offload (midday; the act of writing IS
                  // the exercise) and energy-audit (morning/midday; one
                  // line per energy dimension). Other variable steps are
                  // checkbox-only physical actions. The evening-only
                  // shapes (Intention set, tomorrow-priority,
                  // highlight-reel, Screen cutoff) were removed in the
                  // July 2026 dead-code sweep — they live in
                  // EveningWindDown.jsx now. Custom-interaction steps
                  // short-circuit the heuristic so it can't turn a
                  // button-based step into an authored form.
                  const titleLower = (step.title || "").toLowerCase();
                  const isPriority  = !isCustomInteraction && !isVariable && titleLower.startsWith("priority");
                  const isBrainDump = !isCustomInteraction && !isVariable && titleLower.startsWith("brain dump");
                  const isProgress  = !isCustomInteraction && !isVariable && titleLower.startsWith("progress");
                  const isWorryOffload = !isCustomInteraction && isVariable && step.id === "worry-offload";
                  const isEnergyAudit = !isCustomInteraction && isVariable && step.id === "energy-audit";
                  const isAuthoredStep =
                    isPriority || isBrainDump || isProgress
                    || isWorryOffload || isEnergyAudit;
                  // Maps step shape → step_type as stored in
                  // protocol_inputs. ``worry_offload`` and
                  // ``energy_audit`` each get their own type so the
                  // ``/protocol-inputs/recent`` read stays cleanly
                  // separable per surface.
                  const stepType = isPriority ? "morning_priority"
                    : isBrainDump ? "brain_dump"
                    : isProgress ? "progress_check"
                    : isWorryOffload ? "worry_offload"
                    : isEnergyAudit ? "energy_audit"
                    : null;
                  const authoredLabel = isPriority
                    ? "Write your priority below or say it aloud"
                    : isBrainDump
                      ? "Write it out or say it aloud"
                      : isProgress
                        ? "Write your progress check below or say it aloud"
                        : isWorryOffload
                          ? "Write what you're carrying"
                          : "Note your energy in one line";
                  const authoredPlaceholder = isPriority
                    ? "One thing. Be specific."
                    : isBrainDump
                      ? "What's on your mind right now..."
                      : isProgress
                        ? "This morning I said I would..."
                        : isWorryOffload
                          ? "Write what you're carrying. Don't solve it. Just get it out."
                          : "Physical: · Mental: · Emotional:";
                  // Per-step confirmation copy surfaced under the Submit
                  // button once the text has been saved. Worry offload's
                  // confirmation reinforces the "let it go" framing — the
                  // response itself signals the release. Morning priority
                  // and energy audit get short, calm confirmations — both
                  // are quick captures, not deep reflections.
                  const submitConfirmation = isWorryOffload
                    ? "Written. Now close it."
                    : isEnergyAudit
                      ? "Noted."
                      : isPriority
                        ? "Noted. This is your anchor for today."
                        : "Noted. This feeds your signal.";
                  const hasSaved = !!stepSaved[step.id];
                  return (
                    <div key={step.id}>
                      <div
                        className={`step-row${done ? " done" : ""}`}
                        data-testid="protocol-step"
                        data-step-id={step.id}
                        data-step-title={step.title}
                        onClick={() => toggleExpand(step.id)}
                      >
                        <div onClick={e => { e.stopPropagation(); }}>
                          {/* Commitment steps don't render a checkbox —
                              this step IS the protocol-completion
                              moment (Evening Wind-Down "Screen cutoff").
                              Followthrough steps still render a (no-op)
                              checkbox so the morning anchor surface
                              keeps its grid alignment with the other
                              steps. Standard steps render the regular
                              tappable checkbox. */}
                          {/* Commitment + Followthrough each used to
                              render an empty 22x22 spacer here so the
                              "tap a checkbox after committing to put
                              your phone down" paradox stayed off-
                              screen. May 19: F&F user feedback was
                              that the missing checkbox read as a
                              broken step rather than a deliberate
                              one. Render the standard checkbox now
                              for visual parity — the textarea below
                              still captures the commitment time and
                              the tap remains optional. */}
                          {isFollowthrough ? (
                            <Checkbox checked={done} onToggle={() => {}} />
                          ) : (
                            <Checkbox checked={done} onToggle={() => toggleStep(step.id)} />
                          )}
                        </div>

                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", justifyContent: "space-between",
                            alignItems: "flex-start" }}>
                            <span style={{ fontSize: 13.5, color: done ? P.textMute : P.text,
                              fontWeight: done ? 300 : 400, lineHeight: 1.45,
                              transition: "color 0.25s", flex: 1, paddingRight: 8,
                              textDecoration: done ? "line-through" : "none" }}>
                              {step.title}
                            </span>
                            <div style={{ display: "flex", alignItems: "center",
                              gap: 6, flexShrink: 0 }}>
                              {isVariable && !step.seen_before && (
                                <span style={{
                                  fontSize: 9, letterSpacing: "0.12em",
                                  textTransform: "uppercase", fontWeight: 600,
                                  padding: "2px 7px", borderRadius: 99,
                                  color: P.primary,
                                  border: `1px solid ${P.primary}`,
                                  background: "transparent",
                                  whiteSpace: "nowrap",
                                }}>
                                  New
                                </span>
                              )}
                              {step.duration_minutes > 0 && (
                                <span style={{ fontSize: 10, color: P.textFaint,
                                  whiteSpace: "nowrap" }}>
                                  {step.duration_minutes} min
                                </span>
                              )}
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                                stroke={P.textFaint} strokeWidth="2" strokeLinecap="round"
                                style={{ transform: isOpen ? "rotate(180deg)" : "none",
                                  transition: "transform 0.2s" }}>
                                <path d="M6 9l6 6 6-6" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>

                      {isOpen && (
                        <div style={{
                          padding: "0 0 14px 36px",
                          animation: "fadeUp 0.25s ease forwards",
                        }}>
                          {/* ── Follow-through branch — Morning Anchor
                              "Screens before bed". Two buttons, both
                              acceptable. "Not quite" is treated as a
                              signal, not a failure. ────────────── */}
                          {isFollowthrough && !hasSaved && (
                            <>
                              <p style={{ fontSize: 13, color: P.text, fontWeight: 400,
                                lineHeight: 1.65, padding: "12px 14px",
                                background: P.surfaceAlt, borderRadius: 10,
                                margin: 0,
                              }}>
                                {step.description}
                              </p>
                              <div style={{
                                marginTop: 12,
                                display: "flex",
                                gap: 10,
                              }}>
                                <button
                                  onClick={() =>
                                    submitStepChoice(step, "screen_followthrough", "yes")
                                  }
                                  style={{
                                    flex: 1,
                                    padding: "12px 16px",
                                    background: P.primary,
                                    color: "#F2EDE3",
                                    border: "none",
                                    borderRadius: 10,
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontSize: 13, fontWeight: 500,
                                    letterSpacing: "0.04em",
                                    cursor: "pointer",
                                  }}
                                >
                                  {step.followthrough_yes_cta || "Yes"}
                                </button>
                                <button
                                  onClick={() =>
                                    submitStepChoice(step, "screen_followthrough", "not_quite")
                                  }
                                  style={{
                                    flex: 1,
                                    padding: "12px 16px",
                                    background: "transparent",
                                    color: P.primary,
                                    border: `1.5px solid ${P.primary}`,
                                    borderRadius: 10,
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontSize: 13, fontWeight: 500,
                                    letterSpacing: "0.04em",
                                    cursor: "pointer",
                                  }}
                                >
                                  {step.followthrough_no_cta || "Not quite"}
                                </button>
                              </div>
                            </>
                          )}
                          {isFollowthrough && hasSaved && (
                            <div
                              role="status"
                              style={{
                                padding: "12px 14px",
                                background: P.surfaceAlt,
                                border: `1px solid ${P.border}`,
                                borderRadius: 10,
                              }}
                            >
                              <p style={{
                                fontSize: 12.5, color: P.textMute,
                                fontStyle: "italic",
                                fontWeight: 300,
                                lineHeight: 1.55,
                                margin: 0,
                              }}>
                                {stepInputs[step.id] === "yes"
                                  ? (step.followthrough_yes_confirmation || "Good.")
                                  : (step.followthrough_no_confirmation || "Noted.")}
                              </p>
                            </div>
                          )}

                          {/* Existing default + authored-step blocks —
                              suppressed entirely for custom interactions
                              so we don't double-render the description or
                              the textarea scaffold. */}
                          {!isCustomInteraction && isAuthoredStep && (
                            <div style={{
                              fontSize: 9, color: P.primary,
                              letterSpacing: "0.16em", textTransform: "uppercase",
                              fontWeight: 500, padding: "0 4px 8px",
                            }}>
                              {authoredLabel}
                            </div>
                          )}
                          {!isCustomInteraction && (
                          <p style={{ fontSize: 12.5, color: P.textMid, fontWeight: 300,
                            lineHeight: 1.65, padding: "10px 14px",
                            background: P.surfaceAlt, borderRadius: 10 }}>
                            {step.description}
                          </p>
                          )}
                          {/* Progress Check resurfaces last night's
                              evening_intention above its textarea — the
                              "prompt context" the brief calls for. Shown
                              only when we have a non-empty prior intention. */}
                          {isProgress && priorEveningIntention ? (
                            <div style={{
                              marginTop: 10,
                              padding: "10px 14px",
                              background: P.surfaceAlt,
                              border: `1px solid ${P.primary}`,
                              borderRadius: 10,
                            }}>
                              <div style={{
                                fontSize: 9, color: P.primary,
                                letterSpacing: "0.16em",
                                textTransform: "uppercase",
                                fontWeight: 500, marginBottom: 6,
                              }}>
                                {priorEveningIntention.label}
                              </div>
                              <p style={{
                                fontSize: 12.5, color: P.text,
                                fontWeight: 300, lineHeight: 1.55,
                                fontStyle: "italic",
                              }}>
                                {priorEveningIntention.text}
                              </p>
                            </div>
                          ) : null}
                          {/* Locked state — user already saved this step's
                              authored text today. Show their response in a
                              read-only pane and an unmistakable completed
                              chip instead of a greyed-but-editable textarea
                              + "Noted ✓" button (which users still read as
                              actionable and re-submit). Lock resets at the
                              next local day via the mount rehydration fetch. */}
                          {isAuthoredStep && hasSaved && (
                            <div
                              aria-readonly="true"
                              style={{
                                marginTop: 10,
                                padding: "12px 14px",
                                background: P.surfaceAlt,
                                border: `1px solid ${P.border}`,
                                borderRadius: 10,
                              }}
                            >
                              <div style={{
                                fontSize: 9, color: P.primary,
                                letterSpacing: "0.16em", textTransform: "uppercase",
                                fontWeight: 500, marginBottom: 6,
                              }}>
                                Your response
                              </div>
                              <p style={{
                                fontSize: 13, color: P.text, fontWeight: 300,
                                lineHeight: 1.55, fontStyle: "italic",
                                whiteSpace: "pre-wrap", wordBreak: "break-word",
                                margin: 0,
                              }}>
                                {stepInputs[step.id] || ""}
                              </p>
                            </div>
                          )}
                          {isAuthoredStep && hasSaved && (
                            <div
                              role="status"
                              style={{
                                marginTop: 10,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                padding: "8px 14px",
                                border: `1.5px solid ${P.primary}`,
                                borderRadius: 10,
                                fontFamily: "'DM Sans', sans-serif",
                                fontSize: 12.5,
                                fontWeight: 500,
                                letterSpacing: "0.04em",
                                color: P.primary,
                                background: "transparent",
                              }}
                            >
                              <span aria-hidden="true">{"\u2714"}</span>
                              Already completed for today
                            </div>
                          )}
                          {isAuthoredStep && hasSaved && (
                            <p style={{
                              marginTop: 8,
                              fontSize: 11.5,
                              color: P.textMute,
                              fontStyle: "italic",
                              lineHeight: 1.5,
                            }}>
                              {submitConfirmation}
                            </p>
                          )}

                          {/* Active state — no save yet today: editable textarea
                              + live Submit button. */}
                          {isAuthoredStep && !hasSaved && (
                            <textarea
                              rows={3}
                              placeholder={authoredPlaceholder}
                              value={stepInputs[step.id] || ""}
                              onChange={e => setStepInputs(prev => ({
                                ...prev, [step.id]: e.target.value,
                              }))}
                              style={{ marginTop: 10 }}
                            />
                          )}
                          {isAuthoredStep && !hasSaved && (
                            <button
                              onClick={() => submitStepInput(step, stepType)}
                              disabled={!(stepInputs[step.id] || "").trim()}
                              style={{
                                marginTop: 10,
                                padding: "10px 16px",
                                background: P.primary,
                                color: "#F2EDE3",
                                border: "none",
                                borderRadius: 10,
                                fontFamily: "'DM Sans', sans-serif",
                                fontSize: 12.5, fontWeight: 500,
                                letterSpacing: "0.04em",
                                cursor: (stepInputs[step.id] || "").trim()
                                  ? "pointer" : "default",
                                opacity: !(stepInputs[step.id] || "").trim()
                                  ? 0.7 : 1,
                                transition: "opacity 0.15s",
                              }}
                            >
                              Submit
                            </button>
                          )}
                          {step.note && (
                            <p style={{ fontSize: 11.5, color: P.primaryMid, fontWeight: 400,
                              lineHeight: 1.55, padding: "6px 14px 0",
                              fontStyle: "italic" }}>
                              {step.note}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Post-protocol affect tap — replaces the old typed
                  reflection. Four pills in a 2x2 grid; tapping one
                  PATCHes /complete/{id}/affect against the most recent
                  step's completion row, then auto-advances to the
                  submitted view 600ms later. ``Skip`` advances
                  immediately and writes nothing.

                  Render-gated on ``affectCompletionId`` so the buttons
                  never appear before a row exists to PATCH against.
                  See the race-recovery + skip-and-advance effects
                  above for what happens when ``allDone`` flips true
                  before the underlying POST returns a row id. */}
              {allDone && affectCompletionId && (
                <>
                  <div style={{
                    height: 8,
                    background: P.surfaceAlt,
                    borderTop: `1px solid ${P.border}`,
                    borderBottom: `1px solid ${P.border}`,
                  }} />
                <div style={{
                  padding: "22px 22px",
                  animation: "fadeUp 0.4s ease forwards",
                }}>
                  {(() => {

                    const AFFECTS = [
                      { value: "settled", emoji: "\u{1F60C}", label: "Settled" },
                      { value: "strong",  emoji: "\u{1F4AA}", label: "Strong"  },
                      { value: "neutral", emoji: "\u{1F610}", label: "Neutral" },
                      { value: "heavy",   emoji: "\u{1F614}", label: "Heavy"   },
                    ];

                    const pillBase = {
                      display: "flex", flexDirection: "column",
                      alignItems: "center", justifyContent: "center",
                      gap: 6,
                      minWidth: 80,
                      padding: "14px 8px",
                      borderRadius: 999,
                      background: "transparent",
                      border: `1.5px solid ${P.border}`,
                      cursor: "pointer",
                      transition: "background 0.15s, border-color 0.15s",
                      fontFamily: "'DM Sans', sans-serif",
                    };
                    const pillSelected = {
                      background: "#F2EDE3",
                      borderColor: "#9E8456",
                    };

                    const tapAffect = async (value) => {
                      if (submitted || selectedAffect) return;
                      setSelectedAffect(value);
                      const completionId = pickCompletionId();
                      if (completionId && active && token) {
                        try {
                          await protocolsApi.setAffect(completionId, value, token);
                        } catch (err) {
                          // Best-effort — surface to Sentry but never
                          // block the user's auto-advance.
                          try {
                            Sentry.captureException(err, {
                              tags: { flow: "protocol_affect_patch" },
                              extra: {
                                completion_id: completionId,
                                affect: value,
                                http_status: err?.status ?? "none",
                              },
                            });
                          } catch { /* noop */ }
                        }
                      } else if (active) {
                        try {
                          Sentry.captureMessage("protocol_affect missing completion_id", {
                            tags: { flow: "protocol_affect_patch" },
                            extra: { protocol_id: active.id, affect: value },
                          });
                        } catch { /* noop */ }
                      }
                      // Advance regardless of PATCH outcome.
                      setTimeout(() => {
                        setSubmitted(true);
                        if (active) {
                          protocolsApi.streak(active.id, token).then(setStreakData).catch(() => {});
                          clearProtoProgress(active.id);
                        }
                      }, 600);
                    };

                    const skip = () => {
                      if (submitted) return;
                      setSubmitted(true);
                      if (active) {
                        protocolsApi.streak(active.id, token).then(setStreakData).catch(() => {});
                        clearProtoProgress(active.id);
                      }
                    };

                    return (
                      <>
                        <div style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 10,
                          marginBottom: 14,
                        }}>
                          {AFFECTS.map(a => {
                            const isSelected = selectedAffect === a.value;
                            return (
                              <button
                                key={a.value}
                                type="button"
                                onClick={() => tapAffect(a.value)}
                                disabled={submitted}
                                aria-pressed={isSelected}
                                style={{
                                  ...pillBase,
                                  ...(isSelected ? pillSelected : null),
                                  cursor: submitted ? "default" : "pointer",
                                  opacity: submitted && !isSelected ? 0.55 : 1,
                                }}
                              >
                                <span style={{ fontSize: 24, lineHeight: 1 }} aria-hidden="true">
                                  {a.emoji}
                                </span>
                                <span style={{
                                  fontSize: 13, fontWeight: 500,
                                  color: "#1C1814", letterSpacing: "0.02em",
                                }}>
                                  {a.label}
                                </span>
                              </button>
                            );
                          })}
                        </div>

                        {!submitted && (
                          <div style={{ textAlign: "center" }}>
                            <button
                              type="button"
                              onClick={skip}
                              style={{
                                background: "none", border: "none",
                                color: P.textMute, fontSize: 12,
                                fontFamily: "'DM Sans', sans-serif",
                                cursor: "pointer", padding: "6px 12px",
                                letterSpacing: "0.02em",
                              }}
                            >
                              Skip
                            </button>
                          </div>
                        )}
                      </>
                    );
                  })()}
                  {submitted && (
                    <p style={{
                      marginTop: 14, fontSize: 12.5, color: P.textMute,
                      fontWeight: 300, lineHeight: 1.6, textAlign: "center",
                      animation: "fadeUp 0.35s ease forwards",
                    }}>
                      Complete. This feeds your signal.
                    </p>
                  )}
                </div>
                </>
              )}

              {/* Progress bar at bottom */}
              <div style={{ height: 3, background: P.surfaceAlt }}>
                <div style={{
                  height: "100%", width: `${pct * 100}%`,
                  background: `linear-gradient(90deg, ${P.primary}, ${P.primaryMid})`,
                  transition: "width 0.4s cubic-bezier(0.4,0,0.2,1)",
                }} />
              </div>
            </div>
          </div>

          {/* Today's Protocols — full chronological list
              (Morning → Midday → Evening). Always renders all three so
              the day's structure is visible regardless of time. The
              active hero above is whichever one matches the current
              time band (or a URL pin); here, the matching card in the
              list shows no badge — the rest carry COMPLETED / MISSED /
              UPCOMING. Every card is tappable, including missed and
              upcoming ones, so users can run a protocol out-of-window
              when they need to. The old "Up Next" surface (which
              filtered to incomplete + hid missed past 21:00) is gone —
              this list replaces it. */}
          <div className="fu d4" style={{ padding: "32px 24px 0" }}>
            <div style={{ display: "flex", justifyContent: "space-between",
              alignItems: "center", marginBottom: 16 }}>
              <span style={{ fontSize: 10, color: P.textMute, letterSpacing: "0.14em",
                textTransform: "uppercase", fontWeight: 500 }}>
                Today
              </span>
            </div>

            {queue.map((proto) => {
              const qDomain = (proto.domain || "").toUpperCase();
              const qStyle = DOMAIN_STYLES[qDomain] || DOMAIN_STYLES.BEH;
              const progress = protocolProgress(
                proto, completionsByProtocol, new Date(), stepCountByProtocol,
              );
              const isCurrent    = progress.state === "not_started"
                                  || progress.state === "in_progress";
              const isDone       = progress.state === "completed";
              const isMissedNow  = progress.state === "missed";
              const isInProgress = progress.state === "in_progress";
              const isUpcoming   = progress.state === "upcoming";
              // Mute completed and missed cards visually so the user's
              // eye lands on the current/upcoming work first. Current
              // stays full-brightness; it's the card they're actively
              // running (also rendered as the hero above).
              const cardOpacity = isDone ? 0.55 : isMissedNow ? 0.75 : 1;
              return (
                <div
                  key={proto.id}
                  className="protocol-card"
                  onClick={() => navigate(`/protocol/${proto.id}`)}
                  style={{ opacity: cardOpacity }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center",
                      gap: 7, marginBottom: 7, flexWrap: "wrap" }}>
                      <span style={{
                        fontSize: 9, letterSpacing: "0.11em", textTransform: "uppercase",
                        fontWeight: 500, padding: "3px 9px", borderRadius: 99,
                        background: qStyle.bg,
                        color: qStyle.color,
                      }}>
                        {qDomain}
                      </span>
                      <span style={{ fontSize: 10, color: P.textFaint }}>
                        {proto.duration_minutes} min
                      </span>
                      {isMissedNow && (
                        <span style={{
                          fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase",
                          fontWeight: 600, padding: "3px 9px", borderRadius: 99,
                          background: "transparent",
                          color: "#9E5A4A",
                          border: "1px solid #9E5A4A",
                        }}>
                          {progress.done > 0
                            ? `Missed · ${progress.done} of ${progress.total}`
                            : "Missed"}
                        </span>
                      )}
                      {isDone && (
                        <span style={{
                          fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase",
                          fontWeight: 600, padding: "3px 9px", borderRadius: 99,
                          background: "transparent",
                          color: P.primary,
                          border: `1px solid ${P.primary}`,
                        }}>
                          {"✓ Completed"}
                        </span>
                      )}
                      {isUpcoming && (
                        <span style={{
                          fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase",
                          fontWeight: 600, padding: "3px 9px", borderRadius: 99,
                          background: "transparent",
                          color: P.textMute,
                          border: `1px solid ${P.border}`,
                        }}>
                          Upcoming
                        </span>
                      )}
                    </div>
                    <div style={{
                      fontFamily: "'Playfair Display', serif",
                      fontSize: 18, fontStyle: "italic", color: P.text,
                      lineHeight: 1.2, marginBottom: 4,
                      textDecoration: isDone ? "line-through" : "none",
                    }}>
                      {proto.name}
                      {isCurrent && proto.id === targetId && (
                        <span style={{
                          marginLeft: 8,
                          fontSize: 10, color: P.primary, fontStyle: "normal",
                          letterSpacing: "0.1em", textTransform: "uppercase",
                          fontWeight: 500, verticalAlign: "middle",
                        }}>
                          · Now
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11.5, color: P.textMute, fontWeight: 300 }}>
                      {proto.description}
                    </div>
                    {/* Progress bar replaces the badge for in-progress
                        cards — the bar IS the status. Rendered only
                        once the user has crossed the 0-step boundary
                        and the window is still current; missed cards
                        use the rust pill above with the same X-of-Y
                        substring so partial credit still surfaces. */}
                    {isInProgress && progress.total > 0 && (
                      <ProgressBar done={progress.done} total={progress.total} />
                    )}
                  </div>

                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                    stroke={P.textFaint} strokeWidth="1.6" strokeLinecap="round">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </div>
              );
            })}
          </div>

        </div>

        {/* Bottom Nav */}
        <BottomNav />

      </div>
    </>
  );
}
